<script>
import { mapGetters } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  LIVE_NOTIFICATION_TOAST_DURATION_MS,
  getNotificationConversationId,
  getNotificationMessagePreview,
  getNotificationSenderName,
  pushToast,
  shouldShowLiveNotificationToast,
} from 'dashboard/helper/liveNotificationToast';

export default {
  name: 'LiveNotificationToasts',
  props: {
    duration: {
      type: Number,
      default: LIVE_NOTIFICATION_TOAST_DURATION_MS,
    },
  },
  data() {
    return {
      toasts: [],
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      notificationMeta: 'notifications/getMeta',
    }),
  },
  mounted() {
    emitter.on(BUS_EVENTS.LIVE_NOTIFICATION_CREATED, this.onNotification);
  },
  beforeDestroy() {
    emitter.off(BUS_EVENTS.LIVE_NOTIFICATION_CREATED, this.onNotification);
    this.toasts.forEach(toast => clearTimeout(toast.timer));
  },
  methods: {
    onNotification(notification) {
      const show = shouldShowLiveNotificationToast({
        notification,
        currentAccountId: this.currentAccountId,
        currentConversationId: this.$route?.params?.conversation_id,
      });
      if (!show) return;

      const key = `${notification.id}-${Date.now()}`;
      const toast = {
        key,
        notification,
        timer: setTimeout(() => this.dismiss(key), this.duration),
      };
      const next = pushToast(this.toasts, toast);
      this.toasts
        .filter(existing => !next.includes(existing))
        .forEach(dropped => clearTimeout(dropped.timer));
      this.toasts = next;
    },
    dismiss(key) {
      const toast = this.toasts.find(item => item.key === key);
      if (toast) clearTimeout(toast.timer);
      this.toasts = this.toasts.filter(item => item.key !== key);
    },
    title(notification) {
      const conversationId = getNotificationConversationId(notification);
      const type = this.$t(
        `NOTIFICATIONS_PAGE.TYPE_LABEL.${notification.notification_type}`
      );
      return `#${conversationId} · ${type}`;
    },
    senderName: getNotificationSenderName,
    messagePreview: getNotificationMessagePreview,
    openConversation(toast) {
      const { notification } = toast;
      const {
        id,
        primary_actor_id: primaryActorId,
        primary_actor_type: primaryActorType,
      } = notification;
      this.$store.dispatch('notifications/read', {
        id,
        primaryActorId,
        primaryActorType,
        unreadCount: this.notificationMeta?.unreadCount || 0,
      });
      this.dismiss(toast.key);
      this.$router
        .push({
          name: 'inbox_conversation',
          params: {
            accountId: this.currentAccountId,
            conversation_id: getNotificationConversationId(notification),
          },
        })
        // Same-route navigation rejects with NavigationDuplicated; ignore it.
        .catch(() => {});
    },
  },
};
</script>

<template>
  <div
    class="live-notification-toasts fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-2 pointer-events-none"
    aria-live="polite"
    role="status"
  >
    <transition-group name="live-toast" tag="div" class="flex flex-col gap-2">
      <button
        v-for="toast in toasts"
        :key="toast.key"
        type="button"
        class="live-toast pointer-events-auto relative flex w-[26rem] max-w-[calc(100vw-2rem)] items-center gap-3 overflow-hidden rounded border border-l-4 border-solid border-woot-200 border-l-woot-500 dark:border-woot-700 dark:border-l-woot-500 bg-white dark:bg-slate-800 px-4 pt-2 pb-3 text-left shadow-xl hover:bg-woot-25 dark:hover:bg-slate-700"
        @click="openConversation(toast)"
      >
        <span
          class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-woot-500 text-white"
        >
          <fluent-icon icon="alert" size="16" />
        </span>
        <span class="flex min-w-0 flex-1 flex-col">
          <span
            class="truncate text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            {{ title(toast.notification) }}
          </span>
          <span
            v-if="senderName(toast.notification)"
            class="truncate text-xs font-medium text-slate-600 dark:text-slate-300"
          >
            {{ senderName(toast.notification) }}
          </span>
          <span class="truncate text-xs text-slate-600 dark:text-slate-300">
            {{ messagePreview(toast.notification) }}
          </span>
        </span>
        <span
          class="live-toast-progress absolute bottom-0 left-0 h-1 w-full bg-woot-500"
          :style="{ animationDuration: `${duration}ms` }"
          aria-hidden="true"
        />
      </button>
    </transition-group>
  </div>
</template>

<style scoped>
.live-toast-enter-active,
.live-toast-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.live-toast-enter,
.live-toast-leave-to {
  opacity: 0;
  transform: translateX(1.5rem);
}

.live-toast-move {
  transition: transform 0.25s ease;
}

.live-toast-progress {
  transform-origin: left;
  animation-name: live-toast-countdown;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes live-toast-countdown {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .live-toast-enter-active,
  .live-toast-leave-active,
  .live-toast-move {
    transition: opacity 0.15s linear;
  }

  .live-toast-enter,
  .live-toast-leave-to {
    transform: none;
  }
}
</style>
